import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import {
  ExternalProviderKind,
  INTEGRATION_INFO,
  SystemApp,
  TimeFormat,
} from "@web-app/types";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);
dayjs.extend(advancedFormat);

const EXTERNAL_PROVIDER_APP: Record<ExternalProviderKind, SystemApp> = {
  DOORDASH: SystemApp.DOORDASH,
  UBER: SystemApp.UBER,
};

const EXTERNAL_PROVIDER_NAME: Record<ExternalProviderKind, string> = {
  DOORDASH: "DoorDash",
  UBER: "Uber",
};

export const externalProviderDisplay = (
  kind: ExternalProviderKind,
): { avatarUrl: string; name: string } => ({
  avatarUrl: INTEGRATION_INFO[EXTERNAL_PROVIDER_APP[kind]]?.url ?? "",
  name: EXTERNAL_PROVIDER_NAME[kind] ?? kind,
});

export const formatCurrency = (amount: number, currency = "USD"): string => {
  try {
    return new Intl.NumberFormat(undefined, {
      currency,
      style: "currency",
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
};

export const formatOptimizationDate = (iso: string): string =>
  iso ? dayjs(iso).format("M/D/YYYY") : "—";

export const formatOptimizationDateTime = (
  value?: Date | string | null,
  timezone?: string,
): string => {
  if (!value) return "—";
  if (!timezone || timezone === "system") {
    return dayjs(value).format(TimeFormat.hour_minute);
  }
  return dayjs.utc(value).tz(timezone).format(TimeFormat.hour_minute_tz);
};

export const metersToMiles = (meters: number | null): string =>
  meters == null ? "—" : `${(meters / 1609.344).toFixed(1)} miles`;

type DispatchableEntity = {
  connectedWorkspaceId?: string | null;
  externalAssignee?: { deletedAt?: Date | string | null } | null;
  externalAssigneeId?: string | null;
};

// An order/route is off the table for the cost optimizer once it has been
// handed off — either delegated to a DSP (connectedWorkspaceId) or dispatched
// to an external provider (Uber/DoorDash write externalAssigneeId, the rest
// write an external service assignee).
//
// Route creation defaults connectedWorkspaceId to the route's own workspace, so
// only a connection pointing at someone else counts as a hand-off.
export const isAlreadyDispatched = (
  entity: DispatchableEntity | null | undefined,
  ownerWorkspaceId: string,
): boolean => {
  if (!entity) return false;
  const delegated =
    !!entity.connectedWorkspaceId &&
    entity.connectedWorkspaceId !== ownerWorkspaceId;
  return (
    delegated ||
    !!entity.externalAssigneeId ||
    !!(entity.externalAssignee && !entity.externalAssignee.deletedAt)
  );
};

export const formatDuration = (seconds: number): string => {
  if (!seconds) return "—";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remaining = minutes % 60;
  return remaining ? `${hours} hr ${remaining} min` : `${hours} hr`;
};
